import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard,
  CalendarCog,
  CalendarClock,
  Sparkles,
  Users,
  CalendarDays,
  LogOut,
  Package,
  ShoppingBag,
  Store,
  FlaskConical,
  Receipt,
  Phone,
  FileText,
  Instagram,
  Gift,
  ClipboardCheck,
} from 'lucide-react';

type NavItem = { to: string; label: string; icon: any; end?: boolean };
type NavGroup = { label?: string; items: NavItem[] };

const GROUPS: NavGroup[] = [
  {
    items: [{ to: '/admin', label: 'לוח בקרה', icon: LayoutDashboard, end: true }],
  },
  {
    label: 'ניהול יומן ותורים',
    items: [
      { to: '/admin/appointments', label: 'יומן ותורים', icon: CalendarDays },
      { to: '/admin/calendar', label: 'הגדרות יומן', icon: CalendarCog },
      { to: '/admin/services', label: 'חוקי שירותים', icon: CalendarClock },
    ],
  },
  {
    label: 'ניהול חנות',
    items: [
      { to: '/admin/orders', label: 'הזמנות', icon: Receipt },
      { to: '/admin/products', label: 'מוצרים', icon: ShoppingBag },
      { to: '/admin/shop-services', label: 'טיפולים', icon: Sparkles },
      { to: '/admin/bundles', label: 'מארזים', icon: Package },
      { to: '/admin/gift-cards', label: 'שובר מתנה', icon: Gift },
    ],
  },
  {
    label: 'לקוחות ופניות',
    items: [
      { to: '/admin/clients', label: 'לקוחות', icon: Users },
      { to: '/admin/personal-rx', label: 'רוקחות אישית', icon: FlaskConical },
      { to: '/admin/health-forms', label: 'הצהרות בריאות', icon: ClipboardCheck },
    ],
  },
  {
    label: 'הגדרות אתר',
    items: [
      { to: '/admin/contacts', label: 'פרטי קשר', icon: Phone },
      { to: '/admin/policies', label: 'מדיניות חנות', icon: FileText },
      { to: '/admin/reels', label: 'רגעים מהקליניקה', icon: Instagram },
    ],
  },
];

export default function AdminLayout() {
  const { session, isAdmin, loading } = useAdminAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!session) navigate('/admin/login', { replace: true });
    else if (isAdmin === false) navigate('/admin/login', { replace: true });
  }, [session, isAdmin, loading, navigate]);

  if (loading || !session || !isAdmin) {
    return <div dir="rtl" className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">טוען…</div>;
  }

  return (
    <div dir="rtl" className="min-h-screen flex bg-neutral-50 text-neutral-900">
      <aside className="w-64 shrink-0 border-l bg-white flex flex-col">
        <div className="px-5 py-4 border-b">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">REHAM Clinic</div>
          <div className="font-semibold">ניהול</div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-4 overflow-y-auto">
          {GROUPS.map((group, gi) => (
            <div key={gi} className="space-y-1">
              {group.label && (
                <div className="px-3 pt-1 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {group.label}
                </div>
              )}
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-2 rounded-md text-sm transition ${
                      isActive ? 'bg-neutral-900 text-white' : 'hover:bg-neutral-100'
                    }`
                  }
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="p-3 border-t">
          <div className="text-xs text-muted-foreground mb-2 truncate">{session.user.email}</div>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start gap-2"
            onClick={async () => {
              await supabase.auth.signOut();
              navigate('/admin/login', { replace: true });
            }}
          >
            <LogOut className="h-4 w-4" /> התנתקות
          </Button>
        </div>
      </aside>
      <main className="flex-1 overflow-x-hidden">
        <div className="max-w-6xl mx-auto p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
